import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Loader2, Plus } from 'lucide-react';
import {
    PageHeader, Button, ListHeader, FilterGroup, SimpleSelect, DateRangeFilter, SearchBar, Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
    StatsGrid,
} from '@/components/shared/ui';
import { Pagination } from '@/components/shared/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminPageTitle } from '@/pages/manager/AdminManagerShell';
import { formatDateShort } from '@/utils/dateUtils';
import { useOrderList, useOrderStatistics } from '../../hooks/useOrder';
import type {
    ApiOrderStatus,
    ApiOrderType,
    ApiPaymentStatus,
    GetOrderListResponseDto,
    GetOrderStatisticsResponseDto,
    OrderListItemDto,
} from '../../types/api/order';
import { detectCurrencyFromOrderListRow, formatCurrency } from '@/lib/currencyFormat';
import { ROUTES } from '@/lib/constants/routes';

const ITEMS_PER_PAGE = 10;

const ORDER_STATUS_FILTER_OPTIONS: ApiOrderStatus[] = [
    'DEMO', 'IN_PROGRESS', 'COMPLETED', 'CONFIRMED', 'FAILED', 'CANCELLED',
];

const PAYMENT_STATUS_FILTER_OPTIONS: ApiPaymentStatus[] = ['UN_INVOICED', 'INVOICED', 'PAID', 'REFUNDED'];

const ORDER_TYPE_TABS = ['all', 'ONLINE', 'ORGANIZATION'] as const;
type OrderTypeTab = typeof ORDER_TYPE_TABS[number];

type PersistedOrderManagementState = {
    activeTab?: OrderTypeTab;
    searchTerm?: string;
    statusFilter?: ApiOrderStatus | '';
    paymentStatusFilter?: ApiPaymentStatus | '';
    startDate?: string;
    endDate?: string;
    currentPage?: number;
    topStatCardEngaged?: boolean;
};

function isOrderTypeTab(value: unknown): value is OrderTypeTab {
    return ORDER_TYPE_TABS.includes(value as OrderTypeTab);
}

function isApiOrderStatus(value: unknown): value is ApiOrderStatus {
    return typeof value === 'string' && ORDER_STATUS_FILTER_OPTIONS.includes(value as ApiOrderStatus);
}

function isApiPaymentStatus(value: unknown): value is ApiPaymentStatus {
    return typeof value === 'string' && PAYMENT_STATUS_FILTER_OPTIONS.includes(value as ApiPaymentStatus);
}

function isYmd(value: unknown): value is string {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function readPersistedOrderManagementState(storageKey: string): PersistedOrderManagementState | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        return {
            ...(isOrderTypeTab(parsed.activeTab) ? { activeTab: parsed.activeTab } : {}),
            ...(typeof parsed.searchTerm === 'string' ? { searchTerm: parsed.searchTerm } : {}),
            ...(parsed.statusFilter === '' || isApiOrderStatus(parsed.statusFilter) ? { statusFilter: parsed.statusFilter } : {}),
            ...(parsed.paymentStatusFilter === '' || isApiPaymentStatus(parsed.paymentStatusFilter) ? { paymentStatusFilter: parsed.paymentStatusFilter } : {}),
            ...(isYmd(parsed.startDate) ? { startDate: parsed.startDate } : {}),
            ...(isYmd(parsed.endDate) ? { endDate: parsed.endDate } : {}),
            ...(typeof parsed.currentPage === 'number' && Number.isFinite(parsed.currentPage) && parsed.currentPage > 0
                ? { currentPage: Math.floor(parsed.currentPage) }
                : {}),
            ...(typeof parsed.topStatCardEngaged === 'boolean' ? { topStatCardEngaged: parsed.topStatCardEngaged } : {}),
        };
    } catch {
        return null;
    }
}

function writePersistedOrderManagementState(storageKey: string, state: PersistedOrderManagementState): void {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
        /* sessionStorage may be unavailable in restricted browser modes */
    }
}

/** 통계 카드: KRW / USD 합계를 StatCard value + subLine 으로 나눔 */
function formatDualCurrencyStatLine(krw: number, usd: number): { value: string; subLine?: string } {
    const hasKrw = krw !== 0;
    const hasUsd = usd !== 0;
    if (hasKrw && hasUsd) {
        return { value: formatCurrency(krw, 'KRW'), subLine: formatCurrency(usd, 'USD') };
    }
    if (hasUsd && !hasKrw) {
        return { value: formatCurrency(usd, 'USD') };
    }
    return { value: formatCurrency(krw, 'KRW') };
}

/** @deprecated 목록 테이블 금액도 이제 통화별로 표시 */

/** GET /order/statistics 응답 → 상단 StatCard 3개 */
function buildOrderStatsCardsFromServerApi(
    api: GetOrderStatisticsResponseDto,
    labels: { allOrders: string; onlineOrder: string; orgOrder: string }
): {
    label: string;
    value: string;
    subLine?: string;
    filterValue: OrderTypeTab;
}[] {
    const row = (
        bucket: { amountKRW: number; amountUSD: number },
        label: string,
        filterValue: OrderTypeTab
    ) => {
        const { value, subLine } = formatDualCurrencyStatLine(bucket.amountKRW, bucket.amountUSD);
        return { label, value, ...(subLine ? { subLine } : {}), filterValue };
    };
    return [
        row(api.total, labels.allOrders, 'all'),
        row(api.online, labels.onlineOrder, 'ONLINE'),
        row(api.corporate, labels.orgOrder, 'ORGANIZATION'),
    ];
}

/**
 * 주문 목록 API가 내려주는 다국어/래핑 필드 → 표시용 문자열
 * OpenAPI는 userName 등을 object로 표기 — 실제로는 string | { value }, { title } 등 혼재
 */
function orderListDisplayString(v: unknown, depth = 0): string {
    if (v == null || depth > 4) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'object' && v !== null) {
        const o = v as Record<string, unknown>;
        for (const k of ['title', 'value', 'str', 'text', 'content', 'name', 'label', 'nickname']) {
            const x = o[k];
            if (typeof x === 'string' && x.trim()) return x.trim();
        }
        const d = o.data;
        if (typeof d === 'string' && d.trim()) return d.trim();
        if (d != null && typeof d === 'object') {
            const inner = orderListDisplayString(d, depth + 1);
            if (inner) return inner;
        }
        for (const val of Object.values(o)) {
            if (typeof val === 'string' && val.trim()) return val.trim();
        }
    }
    return '';
}

type OrderListRow = OrderListItemDto & Record<string, unknown>;

/** 목록 주문자명: 기관주문=기관명, 온라인=사용자명 */
function formatOrderListOrdererName(item: OrderListItemDto): string {
    const row = item as OrderListRow;
    if (item.orderType === 'ORGANIZATION') {
        const org = orderListDisplayString(row.organizationTitle);
        if (org) return org;
        /** 목록에 구매기관명이 비어 있고 userName만 채운 백엔드 대비 */
        const fallback = orderListDisplayString(item.userName);
        return fallback || '-';
    }
    return orderListDisplayString(item.userName) || '-';
}

function formatOrderListProductSummary(item: OrderListItemDto, productTypeLabel: Record<string, string>): string {
    const names = item.productNames;
    if (Array.isArray(names) && names.length > 0) {
        const joined = names.map((s) => String(s).trim()).filter(Boolean).join(', ');
        if (joined) return joined;
    }
    const single = item.productName?.trim();
    if (single) return single;
    const types = item.productTypes ?? [];
    if (types.length > 0) {
        return types.map((t) => productTypeLabel[t] ?? t).join(', ');
    }
    return '-';
}

function addAmountToKrwUsdBucket(
    bucket: { krw: number; usd: number },
    amount: number,
    currencyCode: string
): void {
    const c = currencyCode.toUpperCase();
    if (c === 'USD') bucket.usd += amount;
    else bucket.krw += amount;
}

/**
 * 목록 행의 합계 필드 사용.
 */
function computeOrderAmounts(
    item: OrderListItemDto
): { regular: number; discounted: number; total: number } {
    return {
        regular: Number(item.regularPriceTotal) || 0,
        discounted: Number(item.discountedPriceTotal) || 0,
        total: Number(item.totalAmount) || 0,
    };
}

export interface OrderManagementProps {
    embedMode?: boolean;
    organizationId?: number;
    organizationName?: string;
    /** 기관 상세 주문: true=파트너(온라인 주문만), false/미설정=고객(기관 주문) */
    organizationIsPartner?: boolean;
}

/**
 * 비임베드: 관리자 주문 관리 화면(`/master/order-management` 등) — GET `/order/list` + 통계.
 * embedMode: OrgPage 등 `?section=orders` — 기관 주문 목록(마스터 포털 `/master/orders` 개인 내역과 별개). 동일 GET `/order/list` + 클라이언트 기관 필터.
 * (카드형 `Orders` 임베드·GET `/order/organization` 은 별도 진입 경로.)
 */
export default function OrderManagement(props?: OrderManagementProps): React.ReactElement {
    const {
        embedMode = false,
        organizationId: propOrganizationId,
        organizationName: propOrganizationName,
        organizationIsPartner = false,
    } = props ?? {};
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('order');
    const adminTitle = useAdminPageTitle();
    const { getCurrentRole } = useAuth();
    const isMaster = getCurrentRole() === 'master';
    const basePath = isMaster ? ROUTES.MASTER.BASE : ROUTES.ADMIN.BASE;
    const labels = useMemo(() => ({
        pageTitle: t('management.pageTitle'),
        loading: t('management.loading'),
        createOrder: t('management.createOrder'),
        loadFailed: t('management.loadFailed'),
        listFetching: t('management.listFetching'),
        statsLoading: t('management.statsLoading'),
        noOrders: t('management.noOrders'),
        noOrdersFilter: t('management.noOrdersFilter'),
        searchPlaceholder: t('management.searchPlaceholder'),
        searchPlaceholderEmbed: t('management.searchPlaceholderEmbed'),
        allOrders: t('management.allOrders'),
        onlineOrder: t('management.onlineOrder'),
        orgOrder: t('management.orgOrder'),
        allSalesStatus: t('management.allSalesStatus'),
        allPaymentStatus: t('management.allPaymentStatus'),
        orderNumber: t('management.orderNumber'),
        orderType: t('management.orderType'),
        productName: t('management.productName'),
        ordererName: t('management.ordererName'),
        productType: t('management.productType'),
        orderHistory: t('management.orderHistory'),
        listPrice: t('management.listPrice'),
        salePrice: t('management.salePrice'),
        orderAmount: t('management.orderAmount'),
        status: t('management.status'),
        paymentStatus: t('management.paymentStatus'),
        licenseStatus: t('management.licenseStatus'),
        createdAt: t('management.createdAt'),
        licenseActive: t('management.licenseActive'),
        licenseInactive: t('management.licenseInactive'),
    }), [t]);
    const orderStatusLabel = useMemo<Record<ApiOrderStatus, string>>(() => ({
        DEMO: t('create.statusDemoEstimate'),
        IN_PROGRESS: t('create.statusInProgress'),
        COMPLETED: t('create.statusComplete'),
        CONFIRMED: t('create.statusConfirmed'),
        FAILED: t('create.statusFailed'),
        CANCELLED: t('create.statusCancelled'),
    }), [t]);
    const paymentStatusLabel = useMemo<Record<ApiPaymentStatus, string>>(() => ({
        UN_INVOICED: t('management.paymentStatusNA'),
        INVOICED: t('management.paymentStatusClaimComplete'),
        PAID: t('management.paymentStatusPaid'),
        REFUNDED: t('management.paymentStatusRefundComplete'),
    }), [t]);
    const orderTypeLabel = useMemo<Record<ApiOrderType, string>>(() => ({
        ONLINE: t('management.orderTypeOnline'),
        ORGANIZATION: t('management.orderTypeInstitution'),
    }), [t]);
    const productTypeLabel = useMemo<Record<string, string>>(() => ({
        CONTENT: t('management.productTypeContent'),
        EQUIPMENT: t('management.productTypeEquipment'),
        CUSTOM_SERVICE: t('management.productTypeCustomService'),
    }), [t]);
    const listStateStorageKey = useMemo(
        () => `medicrew:order-management:list-state:${basePath}:v1`,
        [basePath]
    );

    const persistedListState = useMemo(
        () => (!embedMode ? readPersistedOrderManagementState(listStateStorageKey) : null),
        [embedMode, listStateStorageKey]
    );

    const [activeTab, setActiveTab] = useState<OrderTypeTab>(persistedListState?.activeTab ?? 'all');

    /** 임베드는 API에 orderType을 넣지 않음(스펙 미정의 organizationId·유형 조합으로 0건 되는 경우 방지). 탭 UI도 숨김. */
    const [searchTerm, setSearchTerm] = useState(persistedListState?.searchTerm ?? '');
    const [statusFilter, setStatusFilter] = useState<ApiOrderStatus | ''>(persistedListState?.statusFilter ?? '');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState<ApiPaymentStatus | ''>(persistedListState?.paymentStatusFilter ?? '');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(persistedListState?.currentPage ?? 1);
    /** 상단 통계카드: 초기엔 아무것도 선택(강조) 안 됨, 한 번 클릭한 뒤에만 isActive */
    const [topStatCardEngaged, setTopStatCardEngaged] = useState(persistedListState?.topStatCardEngaged ?? false);
    const didMountPageResetRef = useRef(false);

    useEffect(() => {
        if (!didMountPageResetRef.current) {
            didMountPageResetRef.current = true;
            return;
        }
        setCurrentPage(1);
    }, [startDate, endDate, statusFilter, paymentStatusFilter, propOrganizationName, organizationIsPartner, embedMode]);

    useEffect(() => {
        if (embedMode) return;
        writePersistedOrderManagementState(listStateStorageKey, {
            activeTab,
            searchTerm,
            statusFilter,
            paymentStatusFilter,
            currentPage,
            topStatCardEngaged,
        });
    }, [
        embedMode,
        listStateStorageKey,
        activeTab,
        searchTerm,
        statusFilter,
        paymentStatusFilter,
        currentPage,
        topStatCardEngaged,
    ]);

    const userSearch = searchTerm.trim();
    
    /** API에 필터 조건 전달 */
    const listParams = useMemo(() => {
        if (embedMode) {
            // 임베드 모드: organizationId로 필터링
            return {
                page: currentPage,
                pageSize: ITEMS_PER_PAGE,
                searchText: userSearch || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                organizationId: propOrganizationId || undefined,
                status: statusFilter || undefined,
                paymentStatus: paymentStatusFilter || undefined,
            };
        }
        // 관리자 모드: API에 필터 조건 전달
        return {
            page: currentPage,
            pageSize: ITEMS_PER_PAGE,
            searchText: userSearch || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            orderType: activeTab !== 'all' ? activeTab : undefined,
            status: statusFilter || undefined,
            paymentStatus: paymentStatusFilter || undefined,
        };
    }, [embedMode, currentPage, userSearch, startDate, endDate, activeTab, statusFilter, paymentStatusFilter, propOrganizationId]);

    /** 상단 카드 금액은 날짜 범위만 반영한 백엔드 통계 그대로 표시 */
    const serverOrderStatsQuery = useOrderStatistics(startDate, endDate, {
        enabled:
            !embedMode
            && Boolean(startDate?.trim() && endDate?.trim()),
    });

    const listQueryKeepPrevious = (previous: GetOrderListResponseDto | undefined) => previous;

    const {
        data: listData,
        isLoading: isListLoading,
        isError: isListError,
        error: listError,
        isFetching: isListFetching,
    } = useOrderList(listParams, {
        /** 쿼리 키(필터·기간) 바뀔 때 목록/화면이 통째로 사라지지 않게 */
        placeholderData: listQueryKeepPrevious,
    });

    const rawOrdersFromList: OrderListItemDto[] = listData?.orders ?? [];

    /** API에서 필터링/페이지네이션된 데이터 그대로 사용 */
    const ordersForView: OrderListItemDto[] = useMemo(() => {
        return rawOrdersFromList;
    }, [rawOrdersFromList]);

    const totalCount = listData?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

    /** 통계 카드 금액: 관리자 화면은 백엔드 `/order/statistics` 응답을 그대로 표시 */
    const orderStats = useMemo(() => {
        if (!embedMode && serverOrderStatsQuery.data) {
            return buildOrderStatsCardsFromServerApi(serverOrderStatsQuery.data, labels);
        }
        if (
            !embedMode
            && serverOrderStatsQuery.isLoading
            && !serverOrderStatsQuery.isError
        ) {
            const loadingRow = (label: string, filterValue: OrderTypeTab) => ({
                label,
                value: '—',
                filterValue,
                subLine: labels.statsLoading,
            });
            return [
                loadingRow(labels.allOrders, 'all'),
                loadingRow(labels.onlineOrder, 'ONLINE'),
                loadingRow(labels.orgOrder, 'ORGANIZATION'),
            ];
        }

        const all = { krw: 0, usd: 0 };
        const online = { krw: 0, usd: 0 };
        const org = { krw: 0, usd: 0 };

        for (const o of rawOrdersFromList) {
            const t = computeOrderAmounts(o).total;
            const cur = detectCurrencyFromOrderListRow(o, null);
            addAmountToKrwUsdBucket(all, t, cur);
            if (o.orderType === 'ONLINE') addAmountToKrwUsdBucket(online, t, cur);
            if (o.orderType === 'ORGANIZATION') addAmountToKrwUsdBucket(org, t, cur);
        }

        const row = (bucket: { krw: number; usd: number }, label: string, filterValue: OrderTypeTab) => {
            const { value, subLine } = formatDualCurrencyStatLine(bucket.krw, bucket.usd);
            return { label, value, ...(subLine ? { subLine } : {}), filterValue };
        };

        return [
            row(all, labels.allOrders, 'all'),
            row(online, labels.onlineOrder, 'ONLINE'),
            row(org, labels.orgOrder, 'ORGANIZATION'),
        ];
    }, [
        embedMode,
        serverOrderStatsQuery.data,
        serverOrderStatsQuery.isLoading,
        serverOrderStatsQuery.isError,
        rawOrdersFromList,
        labels,
    ]);

    const statsItemsWithClick = useMemo(() =>
        orderStats.map(({ filterValue, ...rest }) => ({
            ...rest,
            onClick: () => {
                setTopStatCardEngaged(true);
                setActiveTab(filterValue);
                setCurrentPage(1);
                /** 상단 통계카드: 선택한 주문유형 + 주문상태「주문확정」으로 하단 셀렉트·목록 동기화 */
                setStatusFilter('CONFIRMED');
            },
            isActive: topStatCardEngaged && activeTab === filterValue,
        })),
        [orderStats, activeTab, topStatCardEngaged]
    );

    /** 기관 상세 임베드: 통계는 현재 페이지 데이터로만 계산 (전체 통계는 별도 API 필요) */
    const embedOrgStatsItems = useMemo(() => {
        if (!embedMode) return null;
        const bucket = { krw: 0, usd: 0 };
        for (const o of ordersForView) {
            const t = computeOrderAmounts(o).total;
            const cur = detectCurrencyFromOrderListRow(o, null);
            addAmountToKrwUsdBucket(bucket, t, cur);
        }
        const { value, subLine: dualSub } = formatDualCurrencyStatLine(bucket.krw, bucket.usd);
        const pageNote = `(현재 페이지: ${ordersForView.length}건 / 전체: ${totalCount}건)`;
        const subLineMerged = [dualSub, pageNote].filter(Boolean).join(' · ') || undefined;
        return [
            {
                label: labels.allOrders,
                value,
                ...(subLineMerged ? { subLine: subLineMerged } : {}),
                isActive: true,
            },
        ];
    }, [embedMode, ordersForView, totalCount, labels]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** 첫 방문·캐시 없을 때만 전체 덮는 로딩(필터/카드/탭 변경 시엔 placeholder로 유지) */
    if (isListLoading && listData == null) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
                <p className="text-gray-500">{labels.loading}</p>
            </div>
        );
    }

    return (
        <>
            {isListError && !embedMode && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" role="alert">
                    {labels.loadFailed}{' '}
                    {listError instanceof Error ? listError.message : String(listError ?? '')}
                </div>
            )}
            {!embedMode && (
                <PageHeader
                    title={adminTitle || labels.pageTitle}
                    titleSize="admin"
                    actions={
                        <div className="flex items-center gap-2">
                            <FilterGroup>
                                <DateRangeFilter
                                    startDate={startDate}
                                    endDate={endDate}
                                    onStartDateChange={(val) => { setStartDate(val); setCurrentPage(1); }}
                                    onEndDateChange={(val) => { setEndDate(val); setCurrentPage(1); }}
                                />
                            </FilterGroup>
                            {!isMaster && (
                                <Button variant="lightdark" size="md" onClick={() => navigate(`${basePath}/order-management/create`)}>
                                    <Plus size={16} className="mr-2" />
                                    {labels.createOrder}
                                </Button>
                            )}
                        </div>
                    }
                />
            )}

            {embedMode && (
                <div className="flex items-center mb-4">
                    <FilterGroup>
                        <DateRangeFilter
                            startDate={startDate}
                            endDate={endDate}
                            onStartDateChange={(val) => { setStartDate(val); setCurrentPage(1); }}
                            onEndDateChange={(val) => { setEndDate(val); setCurrentPage(1); }}
                        />
                    </FilterGroup>
                </div>
            )}

            <div className="space-y-6">
                <StatsGrid items={embedOrgStatsItems ?? statsItemsWithClick} columns={3} />

                <div
                    className={isListFetching && !embedMode ? 'relative rounded-lg transition-opacity opacity-80' : undefined}
                >
                    {isListFetching && !embedMode && (
                        <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                            <Loader2 className="w-4 h-4 animate-spin text-brand-500 shrink-0" aria-hidden />
                            <span>{labels.listFetching}</span>
                        </div>
                    )}
                    <ListHeader
                        title={embedMode ? labels.orderHistory : undefined}
                        totalCount={totalCount}
                        rightContent={
                            <div className="flex items-center gap-2 flex-nowrap flex-none">
                                <SimpleSelect
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value as ApiOrderStatus | ''); setCurrentPage(1); }}
                                    wrapperClassName="min-w-[120px]"
                                >
                                    <option value="">{labels.allSalesStatus}</option>
                                    {ORDER_STATUS_FILTER_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{orderStatusLabel[opt]}</option>
                                    ))}
                                </SimpleSelect>
                                <SimpleSelect
                                    value={paymentStatusFilter}
                                    onChange={(e) => { setPaymentStatusFilter(e.target.value as ApiPaymentStatus | ''); setCurrentPage(1); }}
                                    wrapperClassName="min-w-[120px]"
                                >
                                    <option value="">{labels.allPaymentStatus}</option>
                                    {PAYMENT_STATUS_FILTER_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{paymentStatusLabel[opt]}</option>
                                    ))}
                                </SimpleSelect>
                                <div className="min-w-[280px] w-80">
                                    <SearchBar
                                        placeholder={embedMode ? labels.searchPlaceholderEmbed : labels.searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                            </div>
                        }
                    />

                    {/* 주문 유형 탭 — 기관 상세(임베드)에서는 주문내역만 보여 주문유형 탭 숨김 */}
                    {!embedMode && (
                        <div className="flex border-b border-gray-200 mb-2">
                            {ORDER_TYPE_TABS.map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                        activeTab === tab
                                            ? 'border-brand-600 text-brand-700'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {tab === 'all' ? labels.allOrders : orderTypeLabel[tab as ApiOrderType]}
                                </button>
                            ))}
                        </div>
                    )}

                    <Table className="min-w-[1100px]">
                        <TableHeader className="bg-brand-50">
                            <TableRow>
                                <TableHead className="text-center min-w-[220px] whitespace-nowrap">
                                    {labels.orderNumber}
                                </TableHead>
                                <TableHead
                                    className={
                                        embedMode
                                            ? 'text-left min-w-[120px] max-w-[200px]'
                                            : 'text-center min-w-[104px] whitespace-nowrap'
                                    }
                                >
                                    {embedMode ? labels.productName : labels.orderType}
                                </TableHead>
                                {!embedMode && (
                                    <TableHead className="text-left min-w-[100px] max-w-[160px]">{labels.ordererName}</TableHead>
                                )}
                                {!embedMode && (
                                    <TableHead className="min-w-[88px] max-w-[112px] whitespace-nowrap">{labels.productType}</TableHead>
                                )}
                                <TableHead className="text-right min-w-[112px] whitespace-nowrap">{labels.listPrice}</TableHead>
                                <TableHead className="text-right min-w-[112px] whitespace-nowrap">{labels.salePrice}</TableHead>
                                <TableHead className="text-right min-w-[120px] whitespace-nowrap">{labels.orderAmount}</TableHead>
                                <TableHead className="text-center min-w-[88px] whitespace-nowrap">{labels.status}</TableHead>
                                <TableHead className="text-center min-w-[88px] whitespace-nowrap">{labels.paymentStatus}</TableHead>
                                <TableHead className="text-center min-w-[118px] whitespace-nowrap">{labels.licenseStatus}</TableHead>
                                <TableHead className="text-center min-w-[100px] whitespace-nowrap">{labels.createdAt}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {ordersForView.length > 0 ? (
                                ordersForView.map((item) => {
                                    const amounts = computeOrderAmounts(item);
                                    const currency = detectCurrencyFromOrderListRow(item, null);
                                    return (
                                    <TableRow key={item.orderId} className="hover:bg-gray-50 text-sm">
                                        <TableCell className="font-mono text-sm whitespace-nowrap align-middle">
                                            <Link
                                                to={`${basePath}/order-management/${item.orderId}`}
                                                className="text-brand-600 hover:underline whitespace-nowrap"
                                            >
                                                {item.orderNumber}
                                            </Link>
                                        </TableCell>
                                        <TableCell className={embedMode ? 'text-left text-gray-800' : 'text-center'}>
                                            {embedMode
                                                ? formatOrderListProductSummary(item, productTypeLabel)
                                                : (orderTypeLabel[item.orderType] ?? item.orderType)}
                                        </TableCell>
                                        {!embedMode && (
                                            <TableCell className="text-left text-gray-800">
                                                {formatOrderListOrdererName(item)}
                                            </TableCell>
                                        )}
                                        {!embedMode && (
                                            <TableCell className="text-gray-700 text-xs">
                                                {(item.productTypes ?? []).map((t: string) => productTypeLabel[t] ?? t).join(', ')}
                                            </TableCell>
                                        )}
                                        <TableCell className="text-right whitespace-nowrap tabular-nums">
                                            {formatCurrency(amounts.regular, currency)}
                                        </TableCell>
                                        <TableCell className="text-right whitespace-nowrap tabular-nums">
                                            {formatCurrency(amounts.discounted, currency)}
                                        </TableCell>
                                        <TableCell className="text-right font-medium whitespace-nowrap tabular-nums">
                                            {formatCurrency(amounts.total, currency)}
                                        </TableCell>
                                        <TableCell className="text-center whitespace-nowrap">
                                            {orderStatusLabel[item.status] ?? item.status}
                                        </TableCell>
                                        <TableCell className="text-center whitespace-nowrap">
                                            {item.paymentStatus ? paymentStatusLabel[item.paymentStatus] : paymentStatusLabel.UN_INVOICED}
                                        </TableCell>
                                        <TableCell className="text-center whitespace-nowrap">
                                            <span className={item.isLicenseActive ? 'text-green-600' : 'text-gray-400'}>
                                                {item.isLicenseActive ? labels.licenseActive : labels.licenseInactive}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-center text-xs whitespace-nowrap">
                                            {formatDateShort(item.createdAt, i18n.language)}
                                        </TableCell>
                                    </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={embedMode ? 9 : 11} className="h-40 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <Package size={40} className="text-gray-300 mb-2" />
                                            <p className="font-medium">
                                                {activeTab === 'all' && !statusFilter && !paymentStatusFilter && !userSearch
                                                    ? labels.noOrders
                                                    : labels.noOrdersFilter}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <div className="flex flex-col items-center justify-center gap-2 pb-8">
                    <p className="text-sm text-gray-500" aria-live="polite">
                        {t('management.paginationSummary', { total: totalCount, current: currentPage, totalPages })}
                    </p>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalCount}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </>
    );
}
